import { createSelector, createSlice } from "@reduxjs/toolkit";
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, DefaultFailureAction, DefaultSuccessAction } from "../common/reducers/utilities";
import { ensureArray } from "../common/utils/arrayUtilities";
import { markFetched } from "../common/hooks/initHook";

export const accreditationOverrideSlice = createSlice({
    name: 'accreditationOverrides',
    initialState: {
        data: {}
    },
    reducers: {
        set: (state, action) => {
            state.data = ensureArray(action.payload).reduce((result, override) => { result[override.id] = override; return result; }, {});
        },
        addOrUpdate: (state, action) => {
            ensureArray(action.payload).forEach(a => {
                state.data[a.id] = a;
            })
        },
        remove: (state, action) => {
            ensureArray(action.payload).forEach(a => {
                if (state.data[a.id]) {
                    delete state.data[a.id];
                }
            });
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => accreditationOverrideSlice.getInitialState());
    }
});

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'accreditationOverride_one', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'accreditationOverride_one');

const fetchAllAccreditationOverrides = network => network.get('/api/v1/accreditationOverrides', accreditationOverrideSlice.actions.set, visualizeFailure('get'));

export const createAccreditationOverride = (network, accreditationOverride) => network.post('/api/v1/accreditationOverride', accreditationOverride, visualizeSuccess('create', accreditationOverrideSlice.actions.addOrUpdate), visualizeFailure('create'));
export const updateAccreditationOverride = (network, delta) => network.put('/api/v1/accreditationOverride', delta, accreditationOverrideSlice.actions.addOrUpdate, visualizeFailure('update')); 
export const deleteAccreditationOverride = (network, id) => network.delete('/api/v1/accreditationOverride/' + window.encodeURIComponent(id), visualizeSuccess('remove', accreditationOverrideSlice.actions.remove), visualizeFailure('remove'));
// #endregion

// #region selectors
const selectAccreditationOverrideData = state => state.accreditationOverrides.data;
const selectAccreditationOverride = id => createSelector([selectAccreditationOverrideData], accreditationOverrides => accreditationOverrides[id]);
const selectAccreditationOverrides = createSelector([selectAccreditationOverrideData], accreditationOverrides => Object.values(accreditationOverrides));
// #endregion

const listen = {
    accreditationOverrideCreated: (accreditationOverride, { dispatch, websocket }) => {
        markFetched(websocket, 'accreditation_override', accreditationOverride.id)
        dispatch(accreditationOverrideSlice.actions.addOrUpdate(accreditationOverride))
    },
    accreditationOverrideUpdated: (accreditationOverride, { dispatch }) => dispatch(accreditationOverrideSlice.actions.addOrUpdate(accreditationOverride)),
    accreditationOverrideRemoved: (accreditationOverride, { dispatch }) => dispatch(accreditationOverrideSlice.actions.remove(accreditationOverride))
}

// #endregion
const initAccreditations = { run: fetchAllAccreditationOverrides, listen };

const cleanup = { root: accreditationOverrideSlice.name, all: state => Object.values(selectAccreditationOverrideData(state)), removeAll: accreditationOverrideSlice.actions.remove, key: override => override.id };

export const [useAccreditationOverrides] = dataHookFactory(selectAccreditationOverrides, initAccreditations, { cleanup });
export const [useAccreditationOverride] = dataHookFactory(selectAccreditationOverride, id => initAccreditations, { cleanup });

const reducer = { name: accreditationOverrideSlice.name, reducer: accreditationOverrideSlice.reducer }
export default reducer;